import React, { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_ATTR = 'data-cf-turnstile-api';

function loadTurnstileScript(): Promise<void> {
    if (typeof window !== 'undefined' && window.turnstile) {
        return Promise.resolve();
    }
    const existing = document.querySelector(`script[${SCRIPT_ATTR}]`);
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('Turnstile script failed')));
        });
    }
    return new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = SCRIPT_SRC;
        s.async = true;
        s.defer = true;
        s.setAttribute(SCRIPT_ATTR, '1');
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Turnstile script failed'));
        document.head.appendChild(s);
    });
}

/**
 * Renders Cloudflare Turnstile; calls onTokenChange when the user completes the challenge (or null on error/expiry).
 */
export const TurnstileWidget: React.FC<{
    siteKey: string;
    onTokenChange: (token: string | null) => void;
}> = ({ siteKey, onTokenChange }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const onTokenChangeRef = useRef(onTokenChange);
    onTokenChangeRef.current = onTokenChange;

    useEffect(() => {
        if (!siteKey.trim()) {
            return;
        }

        let cancelled = false;

        const run = async () => {
            try {
                await loadTurnstileScript();
            } catch {
                onTokenChangeRef.current(null);
                return;
            }
            if (cancelled || !containerRef.current || !window.turnstile) {
                return;
            }
            containerRef.current.innerHTML = '';
            const id = window.turnstile.render(containerRef.current, {
                sitekey: siteKey,
                callback: (token: string) => onTokenChangeRef.current(token),
                'error-callback': () => onTokenChangeRef.current(null),
                'expired-callback': () => onTokenChangeRef.current(null),
            });
            widgetIdRef.current = id;
        };

        void run();

        return () => {
            cancelled = true;
            const wid = widgetIdRef.current;
            widgetIdRef.current = null;
            if (wid && window.turnstile?.remove) {
                try {
                    window.turnstile.remove(wid);
                } catch {
                    /* ignore */
                }
            }
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
        };
    }, [siteKey]);

    if (!siteKey.trim()) {
        return null;
    }

    return <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center' }} />;
};
